import React, { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import { apiClient } from "../App";
import { toast } from "sonner";
import { RichTextEditor } from "../components/RichTextEditor";
import { usePlanLimits } from "../hooks/usePlanLimits";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Plus, 
  Edit2, 
  Trash2, 
  FileText,
  Copy,
  Tag,
  Eye,
  User
} from "lucide-react";

const PLACEHOLDERS = [
  { label: "Name", value: "{{name}}" },
  { label: "First Name", value: "{{first_name}}" },
  { label: "Last Name", value: "{{last_name}}" },
  { label: "Business Name", value: "{{business_name}}" },
  { label: "Location", value: "{{location}}" },
  { label: "Address", value: "{{address}}" },
  { label: "Postcode", value: "{{postcode}}" },
  { label: "Category", value: "{{category}}" },
  { label: "Email", value: "{{email}}" },
  { label: "Website", value: "{{website}}" },
  { label: "Phone", value: "{{phone_number}}" },
  { label: "Company Phone", value: "{{company_phone_number}}" },
];

// Default sample contact for preview
const DEFAULT_SAMPLE_CONTACT = {
  id: "sample",
  name: "John Smith",
  first_name: "John",
  last_name: "Smith",
  business_name: "Smith Plumbing Services",
  location: "London",
  address: "123 High Street",
  postcode: "SW1A 1AA",
  category: "Plumber",
  email: "john@smithplumbing.co.uk",
  website: "www.smithplumbing.co.uk",
  phone_number: "07700 900123",
  company_phone_number: "020 7123 4567",
};

export const TemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [formData, setFormData] = useState({ name: "", subject: "", body: "" });
  const [saving, setSaving] = useState(false);
  
  // Use the shared plan limits hook
  const { limits, isAtLimit, isUnlimited } = usePlanLimits();
  const maxTemplates = limits.maxTemplates;

  // Preview state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [selectedContactId, setSelectedContactId] = useState("sample");
  const [contactSearch, setContactSearch] = useState("");

  useEffect(() => {
    fetchTemplates();
    fetchContacts();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get("/templates");
      setTemplates(response.data);
    } catch (error) {
      toast.error("Failed to fetch templates");
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await apiClient.get("/contacts?page_size=100");
      const contactsList = response.data.contacts || response.data;
      // Only include contacts with email addresses
      setContacts(contactsList.filter(c => c.email && c.email.trim() !== ""));
    } catch (error) {
      console.error("Failed to fetch contacts for preview", error);
    }
  };

  const openCreateDialog = () => {
    setEditingTemplate(null);
    setFormData({ name: "", subject: "", body: "" });
    setDialogOpen(true);
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.subject || !formData.body) {
      toast.error("Please fill in all fields");
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await apiClient.put(`/templates/${editingTemplate.id}`, formData);
        toast.success("Template updated");
      } else {
        await apiClient.post("/templates", formData);
        toast.success("Template created");
      }
      setDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!templateToDelete) return;
    try {
      await apiClient.delete(`/templates/${templateToDelete}`);
      toast.success("Template deleted");
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to delete template");
    }
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };

  const insertPlaceholder = (placeholder) => {
    setFormData((prev) => ({
      ...prev,
      body: prev.body + placeholder,
    }));
  };

  const copyPlaceholder = (placeholder) => {
    navigator.clipboard.writeText(placeholder);
    toast.success(`Copied ${placeholder}`);
  };

  const openPreviewDialog = (template) => {
    setPreviewTemplate(template);
    setSelectedContactId("sample");
    setContactSearch("");
    setPreviewDialogOpen(true);
  };

  const getSelectedContact = () => {
    if (selectedContactId === "sample") {
      return DEFAULT_SAMPLE_CONTACT;
    }
    return contacts.find(c => c.id === selectedContactId) || DEFAULT_SAMPLE_CONTACT;
  };

  const replacePlaceholders = (text, contact) => {
    if (!text) return "";
    
    const replacements = {
      '{{name}}': contact.name || '',
      '{{first_name}}': contact.first_name || '',
      '{{last_name}}': contact.last_name || '',
      '{{business_name}}': contact.business_name || '',
      '{{location}}': contact.location || '',
      '{{address}}': contact.address || '',
      '{{postcode}}': contact.postcode || '',
      '{{category}}': contact.category || '',
      '{{email}}': contact.email || '',
      '{{website}}': contact.website || '',
      '{{phone_number}}': contact.phone_number || '',
      '{{company_phone_number}}': contact.company_phone_number || '',
    };

    let result = text;
    for (const [placeholder, value] of Object.entries(replacements)) {
      result = result.split(placeholder).join(value);
    }
    return result;
  };

  const getFilteredContacts = () => {
    if (!contactSearch) return contacts.slice(0, 20);
    const search = contactSearch.toLowerCase();
    return contacts.filter(c => 
      (c.email && c.email.toLowerCase().includes(search)) ||
      (c.business_name && c.business_name.toLowerCase().includes(search)) ||
      (c.name && c.name.toLowerCase().includes(search)) ||
      (c.location && c.location.toLowerCase().includes(search))
    ).slice(0, 20);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight text-slate-900">Templates</h1>
          <p className="text-slate-500 mt-1">
            Create and manage email templates ({maxTemplates !== null ? `${templates.length}/${maxTemplates}` : `${templates.length}`})
            {maxTemplates === null && <span className="text-sky-600 ml-1">• Unlimited</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {maxTemplates !== null && templates.length >= maxTemplates && (
            <div className="text-sm text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-200">
              Template limit reached • <a href="/billing" className="underline font-medium hover:text-amber-700">Upgrade plan</a>
            </div>
          )}
          <Button
            onClick={openCreateDialog}
            disabled={maxTemplates !== null && templates.length >= maxTemplates}
            className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="create-template-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Template
          </Button>
        </div>
      </div>

      {/* Placeholders Reference */}
      <Card className="border-slate-200 bg-slate-50">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-3">
            <Tag className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Available Placeholders</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {PLACEHOLDERS.map((p) => (
              <button
                key={p.value}
                onClick={() => copyPlaceholder(p.value)}
                className="px-3 py-1.5 bg-white border border-slate-200 rounded text-sm font-mono text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-colors"
                data-testid={`placeholder-${p.label.toLowerCase().replace(' ', '-')}`}
              >
                {p.value}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-2 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
        </div>
      ) : templates.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileText className="w-12 h-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No templates yet</h3>
            <p className="text-slate-500 text-center max-w-md mb-4">
              Create your first email template with personalized placeholders
            </p>
            <Button onClick={openCreateDialog} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <Card 
              key={template.id} 
              className="border-slate-200 hover:shadow-md transition-shadow"
              data-testid={`template-card-${template.id}`}
            >
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center justify-between">
                  <span className="truncate">{template.name}</span>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openPreviewDialog(template)}
                      className="text-slate-400 hover:text-emerald-600"
                      data-testid={`preview-template-${template.id}`}
                      title="Preview with sample data"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(template)}
                      className="text-slate-400 hover:text-slate-600"
                      data-testid={`edit-template-${template.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setTemplateToDelete(template.id);
                        setDeleteDialogOpen(true);
                      }}
                      className="text-slate-400 hover:text-red-600"
                      data-testid={`delete-template-${template.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-2">
                  <span className="font-medium">Subject:</span> {template.subject}
                </p>
                <div 
                  className="text-sm text-slate-500 line-clamp-3 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template.body) }}
                />
                <p className="text-xs text-slate-400 mt-3">
                  Updated {new Date(template.updated_at).toLocaleDateString()}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingTemplate ? "Edit Template" : "Create Template"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Template Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Welcome Email"
                className="mt-1.5"
                data-testid="template-name-input"
              />
            </div>
            <div>
              <Label htmlFor="subject">Email Subject</Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="e.g., Hello {{name}}, we have an offer for you!"
                className="mt-1.5"
                data-testid="template-subject-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label>Email Body</Label>
                <div className="flex gap-1">
                  {PLACEHOLDERS.slice(0, 4).map((p) => (
                    <button
                      key={p.value}
                      onClick={() => insertPlaceholder(p.value)}
                      className="px-2 py-1 bg-slate-100 text-xs font-mono text-slate-600 rounded hover:bg-slate-200"
                    >
                      + {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <RichTextEditor
                value={formData.body}
                onChange={(value) => setFormData({ ...formData, body: value })}
                data-testid="template-body-editor"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="save-template-btn"
            >
              {saving ? "Saving..." : editingTemplate ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this template? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-template-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Template Preview
            </DialogTitle>
            <DialogDescription>
              See how your email will look with real contact data
            </DialogDescription>
          </DialogHeader>

          {previewTemplate && (
            <div className="space-y-6 py-4">
              {/* Contact Selector */}
              <Card className="border-slate-200 bg-slate-50">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="w-4 h-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-700">Preview with Contact</span>
                  </div>
                  
                  <div className="space-y-3">
                    <Select value={selectedContactId} onValueChange={setSelectedContactId}>
                      <SelectTrigger data-testid="preview-contact-select">
                        <SelectValue placeholder="Select a contact" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sample">
                          <span className="flex items-center gap-2">
                            <span className="text-emerald-600 font-medium">Sample Contact</span>
                            <span className="text-slate-400 text-xs">- John Smith (Demo)</span>
                          </span>
                        </SelectItem>
                        {contacts.length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs text-slate-500 font-medium border-t mt-1 pt-2">
                              Your Contacts
                            </div>
                            {getFilteredContacts().map(contact => (
                              <SelectItem key={contact.id} value={contact.id}>
                                <span className="flex items-center gap-2">
                                  <span>{contact.business_name || contact.name || contact.email}</span>
                                  {contact.location && (
                                    <span className="text-slate-400 text-xs">- {contact.location}</span>
                                  )}
                                </span>
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>

                    {/* Selected Contact Info */}
                    <div className="bg-white rounded-md border border-slate-200 p-3">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {(() => {
                          const contact = getSelectedContact();
                          return (
                            <>
                              <div><span className="text-slate-500">Name:</span> <span className="font-medium">{contact.name || '-'}</span></div>
                              <div><span className="text-slate-500">Business:</span> <span className="font-medium">{contact.business_name || '-'}</span></div>
                              <div><span className="text-slate-500">Email:</span> <span className="font-medium">{contact.email || '-'}</span></div>
                              <div><span className="text-slate-500">Location:</span> <span className="font-medium">{contact.location || '-'}</span></div>
                              <div><span className="text-slate-500">Phone:</span> <span className="font-medium">{contact.phone_number || '-'}</span></div>
                              <div><span className="text-slate-500">Category:</span> <span className="font-medium">{contact.category || '-'}</span></div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Email Preview */}
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-500 text-sm">Subject Line</Label>
                  <div className="mt-1 p-3 bg-slate-100 rounded-md border border-slate-200">
                    <p className="font-medium text-slate-900" data-testid="preview-subject">
                      {replacePlaceholders(previewTemplate.subject, getSelectedContact())}
                    </p>
                  </div>
                </div>

                <div>
                  <Label className="text-slate-500 text-sm">Email Body</Label>
                  <div 
                    className="mt-1 p-4 bg-white rounded-md border border-slate-200 prose prose-sm max-w-none min-h-[200px]"
                    dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(replacePlaceholders(previewTemplate.body, getSelectedContact()))
                    }}
                    data-testid="preview-body"
                  />
                </div>
              </div>

              {/* Placeholder Legend */}
              <div className="text-xs text-slate-500 border-t pt-4">
                <span className="font-medium">Tip:</span> Placeholders like {`{{name}}`} are replaced with actual contact data when sending emails.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                setPreviewDialogOpen(false);
                openEditDialog(previewTemplate);
              }}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="edit-from-preview-btn"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Edit Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
